import React, {useState} from 'react';
import {BaseModal} from 'components/modals';

import {resolveDataLabelPosition} from '@/views/storyboard/utils/chartDataLabels';
import {getChartSettingsFieldDefaults} from '@/views/storyboard/utils/chartSettingsDefaults';
import {Chart, ChartQuery, ChartSettings} from 'entities/chart';
import {ChartSettingsModalContents} from '@/views/storyboard/chart_settings/ChartSettingsModalContents';
import {chartQueryDefaultProps} from '@/views/storyboard/utils/chartPropTypes';
import ChartSettingsErrorBoundary from './ChartSettingsErrorBoundary';

interface Props {
    chartSettings?: ((comment?: boolean) => ChartSettings) | ChartSettings;
    chartQuery?: ChartQuery;
    onSave: (chartData: Chart) => void;
    activeStoryboardId: number | null;
    onModalHide: () => void;
    modalProps: {show: boolean};
    comment?: boolean;
}

const getChartSettingsDefaults: (comment?: boolean) => ChartSettings = getChartSettingsFieldDefaults;
const chartQueryDefaults: ChartQuery = chartQueryDefaultProps;

const ChartSettingsModal = React.memo<Props>((
    {
        chartSettings = getChartSettingsDefaults,
        chartQuery = chartQueryDefaults,
        onSave,
        activeStoryboardId = null,
        onModalHide,
        modalProps,
        comment = false,
    },
) => {
    const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
    const [onModalClose, setOnModalClose] = useState<() => void>(() => () => { });

    let chartSettingsValues;
    if (typeof chartSettings === 'function') {
        chartSettingsValues = chartSettings(comment);
    } else {
        chartSettingsValues = chartSettings;
    }

    const searchQuery = {...chartQueryDefaultProps, ...chartQuery};
    const mergedChartSettings: ChartSettings = {
        ...getChartSettingsDefaults(comment),
        ...chartSettingsValues,
        storyboardId: activeStoryboardId!,
        dataLabelPosition: resolveDataLabelPosition(
            chartSettingsValues.type,
            chartSettingsValues.dataLabelPosition,
        ),
    };

    let dialogClassNames = '';
    if (!comment) {
        dialogClassNames += ' modal-80w modal-60h';
    }

    let styleIfAdditionalDialogOpen;
    if (confirmationOpen) {
        styleIfAdditionalDialogOpen = {zIndex: 100, overflow: 'visible'};
    }

    return (
        <BaseModal
            isOpen={modalProps.show}
            onClose={onModalClose}
            hideHeader={true}
            dialogClassName={dialogClassNames}
            style={styleIfAdditionalDialogOpen}
        >
            <ChartSettingsErrorBoundary>
                <ChartSettingsModalContents
                    initialChartSettingsValues={mergedChartSettings}
                    initialChartQueryValues={searchQuery}
                    comment={comment}
                    setOnModalClose={setOnModalClose}
                    confirmationOpen={confirmationOpen}
                    setConfirmationOpen={setConfirmationOpen}
                    onModalHide={onModalHide}
                    onSave={onSave}
                />
            </ChartSettingsErrorBoundary>
        </BaseModal>
    );
});

export default ChartSettingsModal;
